import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useBridge } from '@onl-web/shared/bridge'
import DaumPostcodeEmbed, { type Address } from 'react-daum-postcode'

import { hasToken } from '@/apis/tokenStore'
import { useCheckOrderAvailability } from '@/query/order/useCheckOrderAvailability'
import { useGetServiceAreaAvailability } from '@/query/serviceArea/useGetServiceAreaAvailability'
import { ROUTES } from '@/router/routes'

import ResultBody from './components/ResultBody'
import { SHARED_ADDRESS_QUERY_KEY } from './constants'
import SearchInputDisplay from './steps/SearchInputDisplay'
import { EmbedWrapper, Overlay, ResultWrapper } from './styles'
import { buildSharedAddress, resolveAddressJibun, toErrorPayload } from './utils'

/**
 * AddressSearchPage — Daum 주소 검색 + 결과 화면 통합 페이지.
 *
 * 라우트: /search
 * 진입 경로:
 *  1) 지도 페이지(/) 의 검색바 클릭 → navigate('/search')  (회원 전용)
 *  2) Airbridge 공유 링크 → /search?address=경기도 연천군 군남면  (mount 시 query 자동 처리)
 * 닫기/뒤로: navigate(ROUTES.ROOT, { replace: true }) — 지도 페이지로 복귀
 *
 * 인증:
 *  /search 는 회원 전용. 토큰 없으면 즉시 메인으로 복귀.
 *  App.tsx 의 useInitAuth 가 ready 시점에만 라우터 마운트하므로 이 페이지 진입 시 토큰 결정 완료 보장.
 *
 * 검증 흐름 (이중 검증):
 *  1) 주소 클릭 직후 — useGetServiceAreaAvailability(query) 자동 호출 (`/service-area/availability`)
 *     - 200 isAvailable=true → AvailableResultStep
 *     - 200 isAvailable=false → UnavailableResultStep
 *     - 4xx/5xx (네트워크 등) → bridge `onError` + 지도 페이지로
 *     - user-context 에러(미납 E422201 / 이미 신청 E409200)는 이 API 에 안 옴 — 2차에서 차단.
 *  2) "수거 신청하기" 클릭 — useCheckOrderAvailability(mutation, `/orders/availability`) 명시 호출
 *     - 200 isAvailable=true → bridge `onApply` (webview 는 /search 유지, 페이지 전환은 네이티브)
 *     - 모든 ApiError → bridge `onError` + 지도 페이지로
 */
export default function AddressSearchPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { callHandler } = useBridge()

  /**
   * 토큰 가드 — mount 시점 1회만 검사.
   * App.tsx 단계에서 토큰 결정이 끝났으므로 여기선 동기 lazy initializer로 충분.
   */
  const [isAuthorized] = useState(() => hasToken())

  /**
   * Airbridge 공유 진입 — mount 시 query 의 address 를 selectedAddress 로 자동 주입.
   * 이후 흐름은 일반 Daum 선택과 동일 (1차 검증 자동 호출 → 결과 화면 분기).
   */
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(() => {
    const sharedAddress = searchParams.get(SHARED_ADDRESS_QUERY_KEY)?.trim()
    return sharedAddress ? buildSharedAddress(sharedAddress) : null
  })

  const addressJibun = resolveAddressJibun(selectedAddress)

  const availability = useGetServiceAreaAvailability({
    address: addressJibun || null,
  })
  const checkBeforeApply = useCheckOrderAvailability()

  /**
   * 결과 화면에 노출할 주소 — 백엔드 정규화 응답(`availability.data.address`) 우선.
   * 응답 도착 전(또는 에러)이면 입력값(addressJibun) 으로 fallback.
   *
   * 입력: "청주시 상당구 하하동" (Daum/Airbridge)
   *  → 백엔드 정규화: "충청북도 청주시 상당구 하하동" (시도 prefix 자동 추가 등)
   * 결과 카드(AddressChip) / waitlist 호출 모두 정규화된 주소 사용해야 일관성 유지.
   */
  const displayAddress = availability.data?.address ?? addressJibun

  const hasResultContext = !!addressJibun

  /** 지도 페이지(ROUTES.ROOT) 로 복귀 — replace 로 stack 정리 */
  const handleBackToMap = () => {
    navigate(ROUTES.ROOT, { replace: true })
  }

  const handleBackToSearch = () => {
    setSelectedAddress(null)
  }

  const handleAddressComplete = (address: Address) => {
    setSelectedAddress(address)
  }

  /**
   * 토큰 가드 — 비회원이면 즉시 메인 복귀.
   * useEffect 로 처리 (렌더 중 navigate 금지).
   */
  useEffect(() => {
    if (!isAuthorized) {
      navigate(ROUTES.ROOT, { replace: true })
    }
  }, [isAuthorized, navigate])

  /**
   * 1차 검증 에러 처리 — `/service-area/availability` 는 200 + isAvailable boolean 으로 분기하므로
   * 에러는 네트워크/4xx/5xx 만. 모두 bridge onError 로 위임 + 지도 복귀.
   * (E422200/E422201/E409200 같은 user-context 에러는 이 API 에 안 옴 — 2차 검증에서 차단)
   */
  useEffect(() => {
    const error = availability.error
    if (!error) return

    void callHandler('onError', toErrorPayload(error))
    navigate(ROUTES.ROOT, { replace: true })
  }, [availability.error, callHandler, navigate])

  /**
   * "수거 신청하기" 클릭 — 2차 검증 후 분기.
   */
  const handleApply = () => {
    if (!selectedAddress || !addressJibun) return

    checkBeforeApply.mutate(
      {
        itemCategory: 'PICKUP',
        addressJibun,
        isQuick: false,
      },
      {
        onSuccess: (data) => {
          if (data.isAvailable) {
            // navigate 호출 없음 — 페이지 전환은 네이티브가 처리, webview 는 /search 유지
            // (native 가 신청 화면 push → 사용자가 뒤로가기 시 결과 화면 그대로 노출)
            void callHandler('onApply', { ...selectedAddress })
          }
          // 200 + isAvailable=false 케이스는 백엔드 정책상 발생하지 않음 (그 경우 422 응답)
        },
        onError: (error) => {
          void callHandler('onError', toErrorPayload(error))
          handleBackToMap()
        },
      }
    )
  }

  // 비회원은 useEffect 에서 redirect — 그 사이엔 빈 화면 (Daum iframe / 결과 화면 둘 다 노출 X)
  if (!isAuthorized) return null

  return (
    <Overlay role='main' aria-label='주소 검색'>
      {hasResultContext ? (
        <ResultWrapper>
          <SearchInputDisplay address={addressJibun} onClear={handleBackToSearch} />
          <ResultBody
            address={displayAddress}
            isLoading={availability.isLoading}
            isAvailable={availability.data?.isAvailable}
            isApplying={checkBeforeApply.isPending}
            onEnterServiceArea={() => {
              // 페이지 전환은 네이티브 책임 — 웹은 핸들러 호출만 (onNextRouter 와 동일 정책)
              void callHandler('enterServiceArea')
            }}
            onApply={handleApply}
          />
        </ResultWrapper>
      ) : (
        // Daum iframe 그대로 — 자체 헤더/X 버튼 미렌더 (가림 한계로 인한 정책).
        // theme prop 으로 색상 톤만 Figma 와 매칭, 사용자 인터랙션은 Daum 자체 검색 인풋 사용.
        <EmbedWrapper>
          <DaumPostcodeEmbed
            onComplete={handleAddressComplete}
            autoClose={false}
            style={{ width: '100%', height: '100%' }}
            theme={{
              bgColor: '#ffffff',
              searchBgColor: '#f5f5f6',
              pageBgColor: '#ffffff',
              textColor: '#1b1c1d',
              queryTextColor: '#1b1c1d',
              postcodeTextColor: '#888888',
              emphTextColor: '#ff5626',
              outlineColor: '#e4e5e7',
            }}
          />
        </EmbedWrapper>
      )}
    </Overlay>
  )
}
